import { X, ShoppingCart, Heart, ExternalLink } from 'lucide-react';
import { useFindCardListings } from '@/hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store';

interface CardInfo {
  id: string;
  name: string;
  set_name?: string;
  game?: string;
  image_url?: string;
}

interface Props {
  card: CardInfo;
  onClose: () => void;
}

export default function FindListingsModal({ card, onClose }: Props) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const { data: listings = [], isLoading } = useFindCardListings(card.id, user?.id);

  const handleGoToListing = (listingId: string) => {
    onClose();
    navigate(`/listings/${listingId}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100">Buy More</h2>
            <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{card.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-600 border-t-transparent" />
            </div>
          ) : listings.length === 0 ? (
            <div className="py-12 text-center">
              <ShoppingCart className="h-12 w-12 text-gray-400 dark:text-zinc-500 mx-auto mb-3" />
              <p className="font-semibold text-gray-700 dark:text-zinc-300">No listings available</p>
              <p className="text-sm text-gray-500 dark:text-zinc-500 mt-1">
                No other sellers are currently listing this card.
              </p>
              <button
                onClick={() => {
                  onClose();
                  navigate('/wishlist');
                }}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 rounded-lg text-sm font-medium hover:bg-brand-100 dark:bg-brand-900/40 transition-colors"
              >
                <Heart className="h-4 w-4" />
                Add to Wishlist
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {listings.map((listing: any) => (
                <div
                  key={listing.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 dark:border-zinc-800 rounded-xl hover:border-gray-200 dark:hover:border-zinc-700 dark:border-zinc-800 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
                >
                  {/* Card image */}
                  <div className="h-14 w-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-800 flex-shrink-0">
                    {listing.card?.image_url ? (
                      <img
                        src={listing.card.image_url}
                        alt={listing.card?.name ?? card.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl">🃏</div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-zinc-200">
                      {listing.condition}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-zinc-500">
                      Qty: {listing.quantity} · by {listing.seller?.username ?? 'Unknown'}
                    </p>
                    {listing.seller?.seller_rating > 0 && (
                      <p className="text-xs text-amber-500">
                        ★ {Number(listing.seller.seller_rating).toFixed(1)}
                      </p>
                    )}
                  </div>

                  {/* Price + action */}
                  <div className="flex-shrink-0 text-right">
                    <p className="text-base font-bold text-gray-900 dark:text-zinc-100">
                      ${Number(listing.price).toFixed(2)}
                    </p>
                    {listing.shipping_price > 0 && (
                      <p className="text-xs text-gray-500 dark:text-zinc-500">
                        +${Number(listing.shipping_price).toFixed(2)} ship
                      </p>
                    )}
                    <button
                      onClick={() => handleGoToListing(listing.id)}
                      className="mt-1.5 flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:text-brand-300 transition-colors"
                    >
                      View <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-zinc-800 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
